import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import NewUserIcon from "../../assets/images/statistic-cards/NewUserIcon";
import TopUserIcon from "../../assets/images/statistic-cards/TopUserIcon";
import TotalUsersIcon from "../../assets/images/statistic-cards/TotalUsersIcon";
import User from "../../assets/images/table/User";
import DataTable, {
  Column,
} from "../../components/common/data-table/DataTable";
import StatisticCard from "../../components/common/statistic-card/StatisticCard";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import styles from "./Users.module.scss";
import { useAuth } from "../../context/AuthContext";
import UsersService from "../../api/services/users.service";
import DeleteModal from "../../components/common/delete-modal/DeleteModal";
import NumberKey from "../../assets/images/table/NumberKey";
import { formatDate } from "../../utils/date";
import Modal from "../../components/common/modal/Modal";
import Select from "../../components/common/select/Select";
import { toast } from "sonner";

interface UserRow {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  profile_photo: string;
  role: string;
}

const Users = () => {
  const { t } = useTranslation("translation");
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [newUsersToday, setNewUsersToday] = useState<number>(0);
  const [topUsers, setTopUsers] = useState<number>(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { setButtons, searchValue } = useAuth();

  const [roleModal, setRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [roleValue, setRoleValue] = useState<string>("");

  // Mirrors accounts/enum.go: RoleType User = "1", Admin = "2", Store = "3".
  const roleOptions = [
    { id: "1", name: t("users.roles.user") as string },
    { id: "2", name: t("users.roles.admin") as string },
    { id: "3", name: t("users.roles.store") as string },
  ];

  const roleLabel = (role: string) =>
    roleOptions.find((option) => option.id === String(role))?.name ||
    (t("users.roles.user") as string);

  const roleClass = (role: string) => {
    switch (String(role)) {
      case "2":
        return styles.role_admin;
      case "3":
        return styles.role_store;
      default:
        return styles.role_user;
    }
  };

  const columns: Column[] = [
    {
      key: "name",
      header: <User />,
      render: (_, row) => (
        <div className={styles.user_cell}>
          {row.profile_photo ? (
            <img
              src={row.profile_photo}
              alt={row.first_name}
              className={styles.user_avatar}
            />
          ) : (
            <div className={styles.no_image}>
              {row.first_name[0]}
              {row.last_name[0]}
            </div>
          )}
          <div className={styles.user_info}>
            <p className={styles.user_name}>
              {row.first_name} {row.last_name}
            </p>
            <a href={`tel:${row?.phone}`} className={styles.user_phone}>
              {row?.phone || ""}
            </a>
            <a href={`mailto:${row?.email}`} className={styles.user_email}>
              {row?.email}
            </a>
          </div>
        </div>
      ),
      filter: true,
    },
    { key: "phone", header: <NumberKey />, filter: true },
    {
      key: "created",
      header: t("users.table.created") as string,
      render: (d) => formatDate(d),
    },
    {
      key: "role",
      header: t("users.table.role") as string,
      render: (role) => (
        <span className={roleClass(role)}>{roleLabel(role)}</span>
      ),
    },
    {
      key: "is_active",
      header: t("users.table.status") as string,
      render: (_, row) => {
        const status = row.is_active;
        let statusClass = "";
        let label = "";
        switch (status) {
          case true:
            statusClass = styles.status_active;
            label = t("users.status.active") as string;
            break;
          case false:
            statusClass = styles.status_deactive;
            label = t("users.status.deactive") as string;
            break;
        }
        return (
          <span className={`${styles.status} ${statusClass}`}>{label}</span>
        );
      },
    },
  ];

  const getUsers = useCallback(async () => {
    if (!searchValue) setLoading(true);
    try {
      const res = await UsersService.getALlUsers(page, limit, searchValue);
      setUsers(res.data.data);
      setCount(res.data.count);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchValue]);

  // Fetch user statistics
  const getUserStatistics = useCallback(async () => {
    try {
      const res = await UsersService.getStatistics();
      setTotalUsers(res.data.total_users);
      setNewUsersToday(res.data.new_users);
      setTopUsers(res.data.top_users);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (count && newPage > Math.ceil(count / limit)) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleOpenRoleModal = (id: number) => {
    const user = users.find((item) => item.id === id);
    if (!user) return;
    setSelectedUser(user);
    setRoleValue(String(user.role));
    setRoleModal(true);
  };

  const handleCloseRoleModal = () => {
    setRoleModal(false);
    setSelectedUser(null);
    setRoleValue("");
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !roleValue) return;
    if (roleValue === String(selectedUser.role)) {
      handleCloseRoleModal();
      return;
    }
    try {
      await UsersService.updateUserRole(selectedUser.id, roleValue);
      toast.success(t("users.role_updated") as string);
      handleCloseRoleModal();
      getUsers();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          (t("users.role_update_error") as string),
      );
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await UsersService.deleteUser(id);
      setDeleteModal(false);
      getUsers();
      getUserStatistics();
    } catch (error) {}
  };

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  useEffect(() => {
    getUserStatistics();
  }, [getUserStatistics]);

  useEffect(() => {
    setButtons([]);
    return () => setButtons([]);
  }, [setButtons]);
  return (
    <div className={styles.users}>
      <TitleUpdater title={t("users.title")} />
      <div>
        <div className={styles.statistic_cards}>
          <StatisticCard
            icon={<TotalUsersIcon />}
            title={t("users.total_users")}
            subtitle={totalUsers.toString()}
          />
          <StatisticCard
            icon={<NewUserIcon />}
            title={t("users.new_users")}
            subtitle={newUsersToday.toString()}
          />
          <StatisticCard
            icon={<TopUserIcon />}
            title={t("users.top_users")}
            subtitle={topUsers.toString()}
          />
        </div>
        {/* <div className={styles.statistic_cards}>
          <StatisticCard
            icon={<AllStoreIcon />}
            title="Total Stores"
            subtitle="250"
          />
          <StatisticCard
            icon={<NewStoreIcon />}
            title="New Stores"
            subtitle="15"
          />
          <StatisticCard
            icon={<TopUserIcon />}
            title="Top Stores"
            subtitle="200"
          />
        </div> */}
        <DataTable
          table_title={t("users.title") as string}
          data={users}
          columns={columns}
          actions
          paginationData={{
            total: count,
            page,
            limit,
            onPageChange: handlePageChange,
            onLimitChange: handleLimitChange,
          }}
          other
          onEdit={handleOpenRoleModal}
          onDelete={(id) => {
            setDeleteModal(true);
            setDeleteId(id);
          }}
          loading={loading}
        />
      </div>
      <Modal
        isOpen={roleModal}
        onClose={handleCloseRoleModal}
        onSubmit={handleUpdateRole}
        title={`${t("users.edit_role_title") as string} ${
          selectedUser
            ? `${selectedUser.first_name} ${selectedUser.last_name}`
            : ""
        }`}
      >
        <div className={styles.modal_content}>
          <Select
            label={t("users.form.role") as string}
            options={roleOptions}
            value={roleValue}
            onChange={(option) => setRoleValue(option.id)}
          />
          <p className={styles.role_hint}>{t("users.role_hint") as string}</p>
        </div>
      </Modal>
      <DeleteModal
        deleteModal={deleteModal}
        onClose={() => setDeleteModal(false)}
        onSubmit={() => handleDelete(Number(deleteId))}
        title={t("users.delete_title") as string}
      />
    </div>
  );
};

export default Users;
